import { ExportOutlined, FilePdfOutlined, PrinterOutlined } from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Empty,
  Flex,
  InputNumber,
  Row,
  Select,
  Spin,
  Table,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { AdminContext } from 'constants/contexts';
import { OrderStatusLabel } from 'constants/status';
import useFetchedData from 'hooks/useFetchedData';
import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { flushSync } from 'react-dom';
import { Link } from 'react-router-dom';
import http, { getHttpErrorMessage } from 'utils/http';
import { downloadResponseBlob, getBlobHttpErrorMessage } from './kitchenSheetDownload';
import {
  buildPullSheetCsvFilename,
  buildPullSheetPdfFilename,
  formatCurrency,
} from './pullSheetExport';
import {
  disablePrintCapture,
  enablePrintCapture,
  prepareBrowserPrint,
  restoreBrowserPrint,
} from './prepBrowserPrint';
import { waitForNextPaint } from './prepSheetPrintBreaks';
import useCateringRestaurants from './useCateringRestaurants';
import usePullSheetChecklist, { buildPullSheetFilterKey } from './usePullSheetChecklist';
import './prep-sheet.css';

const { RangePicker } = DatePicker;

const STATUS_OPTIONS = Object.entries(OrderStatusLabel).map(([statusId, label]) => ({
  value: Number(statusId),
  label,
}));

const PULL_SHEET_PAGE_SIZE = 15;

function formatOrderCount(count) {
  if (count == null || count === '') {
    return null;
  }
  const n = Number(count);
  if (Number.isNaN(n)) {
    return null;
  }
  return `${n} ${n === 1 ? 'order' : 'orders'}`;
}

function formatDateRange(dateRange) {
  if (!dateRange?.[0] || !dateRange?.[1]) {
    return '';
  }
  const start = dateRange[0].format('MMM D, YYYY');
  const end = dateRange[1].format('MMM D, YYYY');
  return start === end ? start : `${start} – ${end}`;
}

function PullSheet() {
  const { message } = App.useApp();
  const { organizationId } = useContext(AdminContext);
  const [dateRange, setDateRange] = useState([dayjs(), dayjs()]);
  const [restaurantIds, setRestaurantIds] = useState([]);
  const [statusIds, setStatusIds] = useState([]);
  const [headcountMin, setHeadcountMin] = useState(null);
  const [headcountMax, setHeadcountMax] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [printIngredients, setPrintIngredients] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const pullSheetRootRef = useRef(null);

  const { restaurantOptions } = useCateringRestaurants();

  const filterError = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) {
      return 'Date range is required.';
    }
    if (dateRange[0].isAfter(dateRange[1], 'day')) {
      return 'Start date must be on or before the end date.';
    }
    if (
      (headcountMin != null && headcountMin < 1)
      || (headcountMax != null && headcountMax < 1)
    ) {
      return 'Headcount must be at least 1 when set.';
    }
    if (
      headcountMin != null
      && headcountMax != null
      && headcountMin > headcountMax
    ) {
      return 'Headcount minimum cannot be greater than the maximum.';
    }
    return null;
  }, [dateRange, headcountMin, headcountMax]);

  const filterQuery = useMemo(() => ({
    start_date: dateRange?.[0]?.format('YYYY-MM-DD'),
    end_date: dateRange?.[1]?.format('YYYY-MM-DD'),
    restaurant_ids: restaurantIds.length ? restaurantIds.join(',') : undefined,
    status: statusIds.length ? statusIds.join(',') : undefined,
    headcount_min: headcountMin != null && headcountMin >= 1 ? headcountMin : undefined,
    headcount_max: headcountMax != null && headcountMax >= 1 ? headcountMax : undefined,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }), [dateRange, restaurantIds, statusIds, headcountMin, headcountMax]);

  const listQuery = useMemo(() => ({
    ...filterQuery,
    page,
    page_size: PULL_SHEET_PAGE_SIZE,
  }), [filterQuery, page]);

  const pullSheetParams = organizationId && !filterError ? {
    url: `v1/organizations/${organizationId}/pull-sheet/`,
    query: listQuery,
  } : null;

  const {
    data: pullSheetData,
    loading: pullSheetLoading,
    error: pullSheetError,
  } = useFetchedData(
    pullSheetParams,
    (response = {}) => {
      const data = response?.data || response;
      const ingredients = data?.results ?? data?.ingredients ?? [];
      const totalCount = data?.count
        ?? data?.summary?.distinct_ingredient_count
        ?? ingredients.length;
      return {
        summary: data?.summary,
        ingredients,
        totalCount,
      };
    },
  );

  const summary = pullSheetData?.summary;
  const categoriesLabel = (summary?.categories || []).join(' · ');
  const ingredients = pullSheetData?.ingredients || [];
  const totalCount = pullSheetData?.totalCount ?? ingredients.length;
  const tableIngredients = printIngredients ?? ingredients;
  const dateRangeLabel = formatDateRange(dateRange);
  const checklistFilterKey = buildPullSheetFilterKey(filterQuery);
  const {
    isChecked,
    setChecked,
    setManyChecked,
    getProgress,
  } = usePullSheetChecklist(
    organizationId,
    checklistFilterKey,
  );
  const pullProgress = getProgress(totalCount);
  const pullProgressPercent = pullProgress.total > 0
    ? Math.round((pullProgress.checked / pullProgress.total) * 100)
    : 0;
  const pageIngredientIds = useMemo(
    () => tableIngredients.map((row) => row.ingredient_id),
    [tableIngredients],
  );
  const allPageChecked = pageIngredientIds.length > 0
    && pageIngredientIds.every((id) => isChecked(id));
  const somePageChecked = pageIngredientIds.some((id) => isChecked(id));

  useEffect(() => {
    setPage(1);
  }, [dateRange, restaurantIds, statusIds, headcountMin, headcountMax]);

  const fetchAllIngredients = useCallback(async () => {
    const pageSize = 100;
    let pageNum = 1;
    let allIngredients = [];
    let total = 0;

    while (true) {
      const response = await http.get(`v1/organizations/${organizationId}/pull-sheet/`, {
        params: {
          ...filterQuery,
          page: pageNum,
          page_size: pageSize,
        },
      });
      const data = response?.data?.data || response?.data || {};
      const batch = data?.results ?? data?.ingredients ?? [];
      total = data?.count ?? data?.summary?.distinct_ingredient_count ?? batch.length;
      allIngredients = allIngredients.concat(batch);
      if (allIngredients.length >= total || batch.length < pageSize) {
        break;
      }
      pageNum += 1;
    }
    return allIngredients;
  }, [organizationId, filterQuery]);

  const onTogglePageIngredients = useCallback((checked) => {
    setManyChecked(pageIngredientIds, checked);
  }, [pageIngredientIds, setManyChecked]);

  const tableColumns = useMemo(() => [
    {
      title: (
        <div className="prep-sheet-checklist-check-cell prep-sheet-no-print">
          <Checkbox
            checked={allPageChecked}
            indeterminate={somePageChecked && !allPageChecked}
            disabled={pageIngredientIds.length === 0}
            onChange={(event) => onTogglePageIngredients(event.target.checked)}
            className="prep-sheet-checklist-checkbox prep-sheet-screen-checkbox"
            aria-label="Select or deselect all ingredients on this page"
          />
        </div>
      ),
      key: 'pulled',
      width: 48,
      className: 'pull-sheet-table-check-col',
      render: (_, record) => (
        <div className="prep-sheet-checklist-check-cell">
          <Checkbox
            checked={isChecked(record.ingredient_id)}
            onChange={(event) => setChecked(
              record.ingredient_id,
              event.target.checked,
            )}
            className="prep-sheet-checklist-checkbox prep-sheet-screen-checkbox"
            aria-label={`Mark ${record.name} as pulled`}
          />
          <span
            className={`prep-sheet-print-checkbox${isChecked(record.ingredient_id) ? ' prep-sheet-print-checkbox--checked' : ''}`}
            aria-hidden
          />
        </div>
      ),
    },
    {
      title: 'Ingredient',
      dataIndex: 'name',
      key: 'name',
      width: '32%',
      ellipsis: true,
    },
    {
      title: 'Category',
      dataIndex: 'category_label',
      key: 'category_label',
      width: '22%',
      ellipsis: true,
    },
    {
      title: 'Quantity to pull (orders)',
      key: 'quantity',
      width: '28%',
      className: 'pull-sheet-quantity-col',
      render: (_, record) => {
        const orderLabel = formatOrderCount(record.order_count);
        return (
          <span className="pull-sheet-quantity-cell">
            <Typography.Text strong className="pull-sheet-quantity-value">
              {`${record.quantity} ${record.unit}`}
            </Typography.Text>
            {orderLabel && (
              <Typography.Text type="secondary" className="pull-sheet-order-count">
                {orderLabel}
              </Typography.Text>
            )}
          </span>
        );
      },
    },
    {
      title: 'Est. cost',
      dataIndex: 'estimated_cost',
      key: 'estimated_cost',
      width: '14%',
      align: 'right',
      className: 'pull-sheet-cost-col',
      render: (value) => formatCurrency(value),
    },
  ], [
    allPageChecked,
    isChecked,
    onTogglePageIngredients,
    pageIngredientIds.length,
    setChecked,
    somePageChecked,
  ]);

  const resetPrintLayout = useCallback(() => {
    disablePrintCapture(pullSheetRootRef.current);
  }, []);

  useEffect(() => {
    const onBeforePrint = () => {
      prepareBrowserPrint();
      enablePrintCapture(pullSheetRootRef.current);
    };
    const onAfterPrint = () => {
      setPrintIngredients(null);
      resetPrintLayout();
      restoreBrowserPrint();
    };
    window.addEventListener('beforeprint', onBeforePrint);
    window.addEventListener('afterprint', onAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', onBeforePrint);
      window.removeEventListener('afterprint', onAfterPrint);
      restoreBrowserPrint();
      disablePrintCapture(pullSheetRootRef.current);
    };
  }, [resetPrintLayout]);

  const preparePrintLayout = useCallback(async () => {
    const allIngredients = await fetchAllIngredients();
    flushSync(() => {
      setPrintIngredients(allIngredients);
    });
    await waitForNextPaint();
  }, [fetchAllIngredients]);

  const handlePrint = async () => {
    if (filterError) {
      message.warning(filterError);
      return;
    }
    setPrintLoading(true);
    try {
      await preparePrintLayout();
      window.print();
    } catch (error) {
      message.error(getHttpErrorMessage(error, false) || 'Unable to prepare pull sheet for print');
    } finally {
      setPrintLoading(false);
    }
  };

  const onExportPdf = async () => {
    if (filterError) {
      message.warning(filterError);
      return;
    }
    setPdfLoading(true);
    try {
      const response = await http.get(`v1/organizations/${organizationId}/pull-sheet/`, {
        params: {
          ...filterQuery,
          export: 'pdf',
        },
        responseType: 'blob',
      });
      downloadResponseBlob(response, buildPullSheetPdfFilename(dateRange));
    } catch (error) {
      const errorMessage = await getBlobHttpErrorMessage(error);
      message.error(errorMessage || 'Unable to export pull sheet PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const onExportCsv = async () => {
    if (filterError) {
      message.warning(filterError);
      return;
    }
    setExporting(true);
    try {
      const response = await http.get(`v1/organizations/${organizationId}/pull-sheet/`, {
        params: {
          ...filterQuery,
          export: 'csv',
        },
        responseType: 'blob',
      });
      downloadResponseBlob(response, buildPullSheetCsvFilename(dateRange));
    } catch (error) {
      const errorMessage = await getBlobHttpErrorMessage(error);
      message.error(errorMessage || 'Unable to export pull sheet');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="prep-sheet-root pull-sheet-root" ref={pullSheetRootRef}>
      <header className="prep-sheet-print-banner prep-sheet-print-only pull-sheet-print-banner">
        <Typography.Title level={3} className="prep-sheet-print-title">
          Pull sheet
        </Typography.Title>
        <Typography.Text className="prep-sheet-print-line">{dateRangeLabel}</Typography.Text>
        {summary && (
          <>
            <Typography.Text className="prep-sheet-print-line">
              {summary.distinct_ingredient_count}
              {' '}
              ingredients ·
              {' '}
              {summary.items_being_prepped}
              {' '}
              items across
              {' '}
              {summary.order_count}
              {' '}
              orders
            </Typography.Text>
            <Typography.Text className="prep-sheet-print-line">
              Est. food cost:
              {' '}
              {formatCurrency(summary.estimated_food_cost)}
            </Typography.Text>
          </>
        )}
        <Typography.Text type="secondary" className="prep-sheet-print-line">
          Generated
          {' '}
          {dayjs().format('MMM D, YYYY [at] h:mm A')}
        </Typography.Text>
      </header>

      <div className="prep-sheet-page-header prep-sheet-no-print">
        <nav className="prep-sheet-breadcrumb" aria-label="Breadcrumb">
          <Link to="/console/prep-kitchen/ingredients">Prep &amp; kitchen</Link>
          <span className="prep-sheet-breadcrumb-sep">&gt;</span>
          <span>Pull sheet</span>
        </nav>
        <Flex justify="space-between" align="flex-start" wrap gap={16}>
          <div>
            <Typography.Title level={2} className="prep-sheet-page-title">
              Pull sheet
            </Typography.Title>
            <Typography.Text type="secondary">
              Consolidated ingredient totals across orders — storage to kitchen.
            </Typography.Text>
          </div>
          <Flex gap={8} wrap>
            <Button
              icon={<PrinterOutlined />}
              onClick={handlePrint}
              loading={printLoading}
              disabled={!organizationId || Boolean(filterError) || !totalCount}
            >
              Print
            </Button>
            <Button
              icon={<FilePdfOutlined />}
              type="primary"
              onClick={onExportPdf}
              loading={pdfLoading}
              disabled={!organizationId || Boolean(filterError) || !totalCount}
            >
              Export PDF
            </Button>
            <Button
              icon={<ExportOutlined />}
              loading={exporting}
              onClick={onExportCsv}
              disabled={!organizationId || Boolean(filterError) || !totalCount}
            >
              CSV
            </Button>
          </Flex>
        </Flex>
      </div>

      <div className="prep-sheet-filter-bar prep-sheet-no-print">
        <div className="prep-sheet-filter-row">
          <div className="prep-sheet-filter-field">
            <span className="prep-sheet-filter-label">Date range *</span>
            <RangePicker
              value={dateRange}
              onChange={(range) => setDateRange(range || [null, null])}
              style={{ width: '100%' }}
            />
          </div>
          <div className="prep-sheet-filter-field">
            <span className="prep-sheet-filter-label">Store</span>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) => (
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              )}
              placeholder="All stores"
              value={restaurantIds}
              options={restaurantOptions}
              onChange={setRestaurantIds}
              style={{ width: '100%' }}
            />
          </div>
          <div className="prep-sheet-filter-field">
            <span className="prep-sheet-filter-label">Order status</span>
            <Select
              mode="multiple"
              allowClear
              showSearch={false}
              placeholder="All statuses"
              value={statusIds}
              options={STATUS_OPTIONS}
              onChange={setStatusIds}
              style={{ width: '100%' }}
            />
          </div>
          <div className="prep-sheet-filter-field prep-sheet-filter-field--compact">
            <span className="prep-sheet-filter-label">Headcount min</span>
            <InputNumber
              min={1}
              placeholder="Min"
              value={headcountMin}
              onChange={(value) => setHeadcountMin(value == null || value < 1 ? null : value)}
              style={{ width: '100%' }}
            />
          </div>
          <div className="prep-sheet-filter-field prep-sheet-filter-field--compact">
            <span className="prep-sheet-filter-label">Headcount max</span>
            <InputNumber
              min={1}
              placeholder="Max"
              value={headcountMax}
              onChange={(value) => setHeadcountMax(value == null || value < 1 ? null : value)}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </div>

      {filterError && (
        <Alert
          type="warning"
          showIcon
          message={filterError}
          className="prep-sheet-no-print"
          style={{ marginBottom: 16 }}
        />
      )}

      {pullSheetError && (
        <Alert
          type="warning"
          showIcon
          message="Unable to load pull sheet."
          description={getHttpErrorMessage(pullSheetError, false)}
          className="prep-sheet-no-print"
          style={{ marginBottom: 16 }}
        />
      )}

      {pullSheetLoading && (
        <Flex align="center" justify="center" className="h-[35vh] prep-sheet-no-print">
          <Spin size="large" />
        </Flex>
      )}

      {!pullSheetLoading && !filterError && summary && (
        <Row gutter={[16, 16]} className="pull-sheet-summary prep-sheet-no-print">
          <Col xs={24} sm={12} lg={6}>
            <div className="pull-sheet-stat-card">
              <span className="pull-sheet-stat-label">Ingredients to pull</span>
              <span className="pull-sheet-stat-value">{summary.distinct_ingredient_count}</span>
              <span className="pull-sheet-stat-sub">distinct items</span>
            </div>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <div className="pull-sheet-stat-card">
              <span className="pull-sheet-stat-label">Items being prepped</span>
              <span className="pull-sheet-stat-value">{summary.items_being_prepped}</span>
              <span className="pull-sheet-stat-sub">
                across
                {' '}
                {summary.order_count}
                {' '}
                orders
              </span>
            </div>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <div className="pull-sheet-stat-card">
              <span className="pull-sheet-stat-label">Est. food cost</span>
              <span className="pull-sheet-stat-value">{formatCurrency(summary.estimated_food_cost)}</span>
              <span className="pull-sheet-stat-sub">based on cost per unit</span>
            </div>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <div className="pull-sheet-stat-card">
              <span className="pull-sheet-stat-label">Categories</span>
              <span className="pull-sheet-stat-value">{summary.categories?.length || 0}</span>
              <span
                className="pull-sheet-stat-sub pull-sheet-categories-list"
                title={categoriesLabel || undefined}
              >
                {categoriesLabel || '—'}
              </span>
            </div>
          </Col>
        </Row>
      )}

      {!pullSheetLoading && !filterError && totalCount === 0 && summary && (
        <div className="prep-sheet-no-print">
          <Empty description="No ingredients to pull for the selected filters." />
        </div>
      )}

      {!pullSheetLoading && !filterError && totalCount > 0 && (
        <div className="pull-sheet-table-wrap">
          <Flex
            justify="space-between"
            align="flex-end"
            wrap
            gap={16}
            className="pull-sheet-table-header prep-sheet-no-print"
          >
            <Typography.Title level={5} className="pull-sheet-section-title">
              Ingredients to pull (storage → kitchen)
            </Typography.Title>
            <div className="pull-sheet-pull-progress">
              <Typography.Text type="secondary" className="prep-sheet-progress-label">
                PULL PROGRESS
              </Typography.Text>
              <Typography.Text strong className="prep-sheet-progress-count">
                {pullProgress.checked}
                {' / '}
                {pullProgress.total}
              </Typography.Text>
              <div className="prep-sheet-progress-track">
                <div
                  className="prep-sheet-progress-fill"
                  style={{ width: `${pullProgressPercent}%` }}
                />
              </div>
            </div>
          </Flex>
          <Table
            className="pull-sheet-table"
            tableLayout="fixed"
            columns={tableColumns}
            dataSource={tableIngredients}
            rowKey="ingredient_id"
            size="middle"
            pagination={printIngredients ? false : {
              current: page,
              pageSize: PULL_SHEET_PAGE_SIZE,
              total: totalCount,
              showSizeChanger: false,
              hideOnSinglePage: false,
              onChange: setPage,
              showTotal: (total, range) => (
                `${range[0]}-${range[1]} of ${total} ingredients`
              ),
            }}
          />
        </div>
      )}
    </div>
  );
}

export default PullSheet;
